import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import SaleCard from "../components/SaleCard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, Tag, Plus, Bell } from "lucide-react";

export default function BuyCars() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const saveSearch = async () => {
    if (!user) { toast.message("Log in to get price-drop alerts"); return; }
    if (!q && !maxPrice) { toast.error("Add a keyword or max price first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/saved-searches", { q: q || null, max_price: maxPrice ? Number(maxPrice) : null });
      toast.success(`Alert saved — ${data.matches} matching now. We'll email you daily about new cars & price drops.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const load = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (maxPrice) p.set("max_price", maxPrice);
    const { data } = await api.get(`/sales?${p.toString()}`);
    setItems(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="buy-cars-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4 rise">
        <div>
          <p className="font-mono text-xs tracking-widest uppercase text-amber-400 mb-1">// Cars for sale</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Own your next earner.</h1>
          <p className="text-slate-400 mt-2 max-w-xl">Gig-proven cars from MY VELOCITY hosts. Message the seller, reserve with a refundable deposit, drive it home.</p>
        </div>
        <Link to="/sell"><Button className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold" data-testid="sell-cta-btn"><Plus className="w-4 h-4 mr-2" />Sell your car</Button></Link>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-8 flex-wrap rise rise-1" data-testid="buy-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search make or model" data-testid="buy-search" className="pl-9 bg-slate-900 border-slate-800" />
        </div>
        <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max price" data-testid="buy-max-price" className="w-36 bg-slate-900 border-slate-800" />
        <Button type="submit" variant="outline" data-testid="buy-apply-filters" className="bg-transparent border-slate-700 hover:bg-slate-800">Apply</Button>
        <Button type="button" variant="outline" onClick={saveSearch} disabled={saving} data-testid="save-search-btn" className="bg-transparent border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"><Bell className="w-3.5 h-3.5 mr-1.5" />Alert me</Button>
      </form>

      {items === null ? <p className="text-slate-400">Loading…</p> : items.length === 0 ? (
        <div className="p-12 rounded-2xl border border-dashed border-slate-800 text-center" data-testid="buy-empty">
          <Tag className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <p className="text-slate-400">No cars for sale yet. Be the first to list one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sale-grid">
          {items.map((l, i) => <SaleCard key={l.listing_id} listing={l} delay={i * 60} />)}
        </div>
      )}
    </main>
  );
}
